import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAssistance, useChimeMeeting } from "../hooks";

export const CameraContext = React.createContext({});

/**
 * The camera context provider
 * 
 * @param {Object} props The props of the component
 * @returns The camera context provider
 */
const CameraProvider = ({ children }) => {
  const [selectedInCameras, setSelectedInCameras] = useState([]);
  const [selectedOutCameras, setSelectedOutCameras] = useState([]);
  const [selectedCameras, setSelectedCameras] = useState([]);
  const { sendControlMessage, audioVideo } = useChimeMeeting();
  const { assistance } = useAssistance();

  const inCameras = useMemo(
    () => assistance?.available_cameras?.in,
    [assistance],
  );
  const outCameras = useMemo(
    () => assistance?.available_cameras?.out,
    [assistance],
  );

  const attendees = useMemo(() => assistance?.attendees, [assistance]);

  const userAttendee = useMemo(() => attendees?.find((attendee => attendee.ExternalUserId?.split("@")?.[0] === "user")), [attendees]);

  /**
   * Handles the attendee presence change
   * 
   * @param {String} attendeeId The attendee ID
   * @param {Boolean} present The attendee presence
   */
  const onAttendeePresenceChange = useCallback((attendeeId, present) => {
    if (present && attendeeId === userAttendee?.AttendeeId) {
      setTimeout(() => {
        sendControlMessage(
          JSON.stringify({
            cameras: selectedCameras,
          }),
        );  
      }, 4000)
    }
  }, [userAttendee, selectedCameras, sendControlMessage]);

  useEffect(() => {
    audioVideo?.realtimeSubscribeToAttendeeIdPresence(onAttendeePresenceChange);
    return () => {
      audioVideo?.realtimeUnsubscribeToAttendeeIdPresence(onAttendeePresenceChange);
    }
  }, [audioVideo, onAttendeePresenceChange]);

  /**
   * Handles the camera selection
   * 
   * @param {String} camera The camera ID
   */
  const onSelectCamera = useCallback(
    (camera) => {
      if (inCameras?.includes(camera)) {
        setSelectedInCameras([camera]);
        setSelectedCameras([camera]);
      } else if (outCameras?.includes(camera)) {
        setSelectedOutCameras([camera]);
        setSelectedCameras([camera]);
      }
      setSelectedCameras([camera]);
    },
    [inCameras, outCameras],
  );

  /**
   * Handles the interior camera selection
   */
  const onSelectInteriorCameras = useCallback(() => {
    setSelectedCameras([selectedInCameras]);
  }, [selectedInCameras]);

  /**
   * Handles the exterior camera selection
   */
  const onSelectExteriorCameras = useCallback(() => {
    setSelectedCameras(selectedOutCameras);
  }, [selectedOutCameras]);

  /**
   * Checks if a camera is selected
   * 
   * @param {String} camera The camera ID
   * @returns {Boolean} True if the camera is selected, false otherwise
   */
  const isCameraSelected = useCallback(
    (camera) =>
      selectedCameras?.includes(camera) ||
      selectedOutCameras?.includes(camera) ||
      selectedInCameras?.includes(camera),
    [selectedCameras, selectedInCameras, selectedOutCameras],
  );

  const resetCameras = useCallback(() => {
    setSelectedInCameras([inCameras?.[0]]);
    setSelectedOutCameras([outCameras?.[0]]);
    setSelectedCameras([]);
  }, [inCameras, outCameras]);

  useEffect(() => {
    sendControlMessage(
      JSON.stringify({
        cameras: selectedCameras,
      }),
    );
  }, [selectedCameras, sendControlMessage]);

  useEffect(() => {
    setSelectedInCameras([inCameras?.[0]]);
    setSelectedOutCameras([outCameras?.[0]]);
  }, [inCameras, outCameras]);

  const contextValue = useMemo(
    () => ({
      onSelectCamera,
      onSelectExteriorCameras,
      onSelectInteriorCameras,
      resetCameras,
      selectedCameras,
      selectedInCameras,
      selectedOutCameras,
      isCameraSelected,
      inCameras,
      outCameras,
    }),
    [
      onSelectCamera,
      onSelectExteriorCameras,
      onSelectInteriorCameras,
      resetCameras,
      selectedCameras,
      selectedInCameras,
      selectedOutCameras,
      isCameraSelected,
      inCameras,
      outCameras,
    ],
  );

  return (
    <CameraContext.Provider value={contextValue}>
      {children}
    </CameraContext.Provider>
  );
};

export default React.memo(CameraProvider);
